/**
 * SLA threshold helper — pure function, no DOM, testable in node.
 *
 * WHY this lives here: the SLA color is the "answer now" signal. A wrong
 * threshold makes a breached lead look fine. Keeping the logic pure and
 * tested makes regressions instantly visible.
 */

export type SlaState = "fresh" | "warn" | "breach";

/** Warn window: 5 minutes before due. */
const WARN_MS = 5 * 60 * 1000;

/**
 * Returns the urgency state for a given due-at timestamp.
 * - `null` / undefined dueAt → `"fresh"` (no SLA set).
 * - past due → `"breach"`.
 * - within 5 minutes of due → `"warn"`.
 * - otherwise → `"fresh"`.
 */
export function slaState(dueAt: string | null | undefined, now: Date = new Date()): SlaState {
  if (!dueAt) return "fresh";
  const due = new Date(dueAt);
  if (!isFinite(due.getTime())) return "fresh";
  const msLeft = due.getTime() - now.getTime();
  if (msLeft <= 0) return "breach";
  if (msLeft <= WARN_MS) return "warn";
  return "fresh";
}

/** Milliseconds remaining until dueAt (negative if breached, 0 if null). */
export function msUntilDue(dueAt: string | null | undefined, now: Date = new Date()): number {
  if (!dueAt) return 0;
  const due = new Date(dueAt);
  if (!isFinite(due.getTime())) return 0;
  return due.getTime() - now.getTime();
}

/** Format a millisecond duration as `m:ss` (always non-negative display). */
export function formatMss(ms: number): string {
  const absMs = Math.abs(ms);
  const totalSeconds = Math.floor(absMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}
